import React, { useState, useRef, useEffect } from "react";
import { imageService } from "../src/services/imageService";

interface ResilientImageProps {
	src: string;
	alt: string;
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
	onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
	onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
	lazy?: boolean;
	fallbackSrc?: string;
	showLoadingSpinner?: boolean;
	retryAttempts?: number;
	retryDelay?: number;
	priority?: boolean; // For above-the-fold images
}

const ResilientImage: React.FC<ResilientImageProps> = ({
	src,
	alt,
	className = "",
	onClick,
	onLoad,
	onError,
	lazy = true,
	fallbackSrc,
	showLoadingSpinner = true,
	retryAttempts = 3,
	retryDelay = 1000,
	priority = false,
}) => {
	const [imageStatus, setImageStatus] = useState<
		"loading" | "loaded" | "error"
	>("loading");
	const [retryCount, setRetryCount] = useState(0);
	const [imageSrc, setImageSrc] = useState<string>(src);
	const [startTime, setStartTime] = useState<number>(Date.now());
	const imgRef = useRef<HTMLImageElement>(null);
	const [isInView, setIsInView] = useState(!lazy || priority);

	useEffect(() => {
		if (!lazy || priority) {
			setIsInView(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsInView(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "50px" } // Load images 50px before they come into view
		);

		if (imgRef.current) {
			observer.observe(imgRef.current);
		}

		return () => observer.disconnect();
	}, [lazy, priority]);

	const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
		const loadTime = Date.now() - (startTime || Date.now());

		// Record metrics
		imageService.addMetric({
			url: imageSrc,
			loadTime,
			status: "success",
			attempts: retryCount + 1,
			timestamp: Date.now(),
		});

		setImageStatus("loaded");
		onLoad?.(e);
	};

	const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
		const imgElement = e.target as HTMLImageElement;
		const loadTime = Date.now() - startTime;

		// If we have retry attempts left, try again with exponential backoff
		if (retryCount < retryAttempts) {
			setRetryCount((prev) => prev + 1);
			const delay = retryDelay * Math.pow(2, retryCount); // Exponential backoff

			// Record retry metric
			imageService.addMetric({
				url: imageSrc,
				loadTime,
				status: "retry",
				attempts: retryCount + 1,
				timestamp: Date.now(),
			});

			setTimeout(() => {
				// Force reload by adding cache-busting parameter
				const separator = src.includes("?") ? "&" : "?";
				const newSrc = `${src}${separator}t=${Date.now()}`;
				setImageSrc(newSrc);
				setStartTime(Date.now()); // Reset start time for new attempt
				setImageStatus("loading");
			}, delay);
			return;
		}

		// Record final error metric
		imageService.addMetric({
			url: imageSrc,
			loadTime,
			status: "error",
			attempts: retryCount + 1,
			timestamp: Date.now(),
		});

		// If all retries failed, try fallback source
		if (fallbackSrc && imgElement.src !== fallbackSrc) {
			imgElement.src = fallbackSrc;
			setImageStatus("loading");
			setRetryCount(0);
			setStartTime(Date.now()); // Reset for fallback attempt
			return;
		}

		// If fallback also failed or no fallback provided, set error state
		setImageStatus("error");
		onError?.(e);
	};

	const generateFallbackAvatar = (name: string) => {
		return `https://ui-avatars.com/api/?name=${encodeURIComponent(
			name
		)}&background=0284c7&color=fff&size=200`;
	};

	const getLoadingPlaceholder = () => {
		return (
			<div className="animate-pulse bg-slate-700 rounded-full flex items-center justify-center">
				<div className="w-6 h-6 bg-slate-600 rounded-full"></div>
			</div>
		);
	};

	const getErrorPlaceholder = () => {
		// Generate a simple SVG placeholder or use a default icon
		return (
			<div className="bg-slate-700 flex items-center justify-center text-slate-400">
				<svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
					<path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
				</svg>
			</div>
		);
	};

	if (!isInView) {
		return (
			<div
				ref={imgRef}
				className={`${className} bg-slate-700 animate-pulse`}
				aria-label={`Loading image: ${alt}`}
			>
				{getLoadingPlaceholder()}
			</div>
		);
	}

	return (
		<div className="relative">
			{imageStatus === "loading" && showLoadingSpinner && (
				<div
					className={`absolute inset-0 ${className} bg-slate-700 animate-pulse flex items-center justify-center`}
				>
					<div className="flex items-center space-x-2">
						<div className="w-4 h-4 bg-slate-600 rounded-full animate-spin"></div>
						<span className="text-sm text-slate-400">טוען...</span>
					</div>
				</div>
			)}

			{imageStatus === "error" && (
				<div
					className={`absolute inset-0 ${className} bg-slate-700 flex items-center justify-center`}
				>
					{getErrorPlaceholder()}
				</div>
			)}

			<img
				ref={imgRef}
				src={imageSrc}
				alt={alt}
				className={`${className} transition-opacity duration-200 ${
					imageStatus === "loaded" ? "opacity-100" : "opacity-0"
				}`}
				onClick={onClick}
				onLoad={handleImageLoad}
				onError={handleImageError}
				loading={lazy && !priority ? "lazy" : "eager"}
			/>
		</div>
	);
};

export default ResilientImage;
